import { openPromptTemplateSelection, applyPromptTemplateSelection, type PromptTemplateSelection } from "../editing-dispatch.js";
import { serializePortablePrompt } from "@seedance/contracts";
import { CanvasPromptEditor } from "../nodes/InlineTextEditor.js";
import { parseExternalPromptText, replacePromptRange, promptLength, promptOffsetToPoint, type PromptRange } from "../prompts/prompt-edit-operations.js";
import { readPromptSelection } from "../prompts/prompt-dom.js";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import type { CanvasDocumentV3 as CanvasDocument } from "@seedance/contracts";
import { canvasCopy } from "../../copy/zh-CN.js";
import {
  PromptTemplatePicker,
  applyPromptTemplate,
  type PromptTemplateApplyMode
} from "../composer/PromptTemplatePicker.js";
import { updateTextNode } from "../seedance-editor.js";

type TextNode = Extract<CanvasDocument["nodes"][number], { kind: "text" }>;

export interface TextNodeInspectorProps {
  document: CanvasDocument;
  textNode: TextNode;
  onChange(document: CanvasDocument): void;
  onUndo?(): void;
  onRedo?(): void;
  onSavePromptTemplate(input: { title: string; content: string }): Promise<void> | void;
  onManagePromptTemplates(): void;
}

export function TextNodeInspector({
  document,
  textNode,
  onChange,
  onSavePromptTemplate,
  onManagePromptTemplates, onUndo, onRedo
}: TextNodeInspectorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [templateSelection, setTemplateSelection] = useState<PromptTemplateSelection>();
  const portable = serializePortablePrompt(textNode.text);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateSaveOpen, setTemplateSaveOpen] = useState(false);
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateStatus, setTemplateStatus] = useState<string>();

  const applyTemplate = (content: string, mode: PromptTemplateApplyMode) => {
    try {
      if (!templateSelection) throw new Error("请重新打开模板选择器。");
      onChange(applyPromptTemplateSelection(document,{nodeId:textNode.id,field:"text",inputEdgeId:null},templateSelection,content,mode));
      setTemplatePickerOpen(false);
    } catch { setTemplateStatus("提示词已更新，原内容已保留，请重新选择插入位置。"); }
  };
  const saveTemplate = async () => {
    const title = templateTitle.trim();
    if (!title || !portable.trim()) return;
    try {
      await onSavePromptTemplate({ title, content: portable });
      setTemplateStatus(canvasCopy.promptTemplateSaved);
      setTemplateTitle("");
      setTemplateSaveOpen(false);
    } catch {
      setTemplateStatus(canvasCopy.promptTemplateSaveFailed);
    }
  };

  return (
    <aside className="node-inspector" aria-label="Text node inspector">
      <header><span>Text node</span><strong>Note</strong></header>
      <section className="node-inspector__settings">
        <label>
          Text
          <div ref={editorRef}><CanvasPromptEditor document={document} source={{nodeId:textNode.id,field:"text",inputEdgeId:null}} onChange={onChange} ariaLabel="Text" maxLength={50_000} {...(onUndo ? {onUndo} : {})} {...(onRedo ? {onRedo} : {})} /></div>
        </label>
        <div className="node-inspector__prompt-tools">
          <button
            type="button"
            disabled={!portable.trim()}
            aria-expanded={templateSaveOpen}
            onClick={() => setTemplateSaveOpen((current) => !current)}
          >
            {canvasCopy.savePromptTemplate}
          </button>
          <button
            type="button"
            aria-expanded={templatePickerOpen}
            onMouseDown={() => { const root = editorRef.current?.querySelector<HTMLElement>("[contenteditable]"); setTemplateSelection(openPromptTemplateSelection(document,{nodeId:textNode.id,field:"text",inputEdgeId:null},root ? readPromptSelection(root,textNode.text) ?? undefined : undefined)); }}
            onClick={() => { if (!templatePickerOpen) setTemplateSelection(openPromptTemplateSelection(document,{nodeId:textNode.id,field:"text",inputEdgeId:null},templateSelection && JSON.stringify(templateSelection.content) === JSON.stringify(textNode.text) && JSON.stringify(templateSelection.source) === JSON.stringify({nodeId:textNode.id,field:"text",inputEdgeId:null}) ? templateSelection.range : undefined)); setTemplatePickerOpen(current=>!current); }}
          >
            {canvasCopy.myPromptTemplates}
          </button>
          <button type="button" aria-label="管理模板" onClick={onManagePromptTemplates}>管理模板</button>
        </div>
        {templateSaveOpen ? (
          <div className="node-inspector__template-save">
            <input
              aria-label={canvasCopy.promptTemplateTitle}
              placeholder={canvasCopy.promptTemplateTitle}
              value={templateTitle}
              maxLength={160}
              onChange={(event) => setTemplateTitle(event.currentTarget.value)}
            />
            <button type="button" disabled={!templateTitle.trim() || !portable.trim()} onClick={() => void saveTemplate()}>
              {canvasCopy.confirmSavePromptTemplate}
            </button>
          </div>
        ) : null}
        <PromptTemplatePicker
          open={templatePickerOpen}
          onApply={applyTemplate}
          onClose={() => setTemplatePickerOpen(false)}
        />
        {templateStatus ? <output role="status">{templateStatus}</output> : null}
      </section>
    </aside>
  );
}
